import { useEffect, useState, type ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { fetchUser, type AdminUserDetail } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { InfoRow, PIIField } from '@/components/DataDisplay'
import { ErrorState, PermissionDenied, Skeleton } from '@/components/States'
import { useAuth } from '@/providers/AuthProvider'

/**
 * A3.09 — Aloqa ma'lumotlari (L2/L3). `GET /admin/users/:id` qayta ishlatiladi.
 * PII (telefon) maskalangan, `PIIField` orqali ochiladi (ochish auditga tushishi
 * kerak — hozir klient-side; server-side reveal-log = follow-up).
 */
export function UserContact() {
  const t = useT()
  const { can } = useAuth()
  const { id = '' } = useParams()
  const [user, setUser] = useState<AdminUserDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchUser(Number(id), ac.signal)
      .then((u) => {
        if (alive) setUser(u)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [id, reload])

  if (!can(PERMISSIONS.USERS_VIEW)) return <PermissionDenied permission={PERMISSIONS.USERS_VIEW} />

  return (
    <>
      <PageHeader
        title={t.users.contact}
        screenId="A3.09"
        actions={
          <Link
            to={`/users/${id}`}
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.users.cardTitle}
          </Link>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {loading ? (
          <div className="max-w-xl space-y-2 rounded-lg border border-border-default bg-surface-2 p-4">
            {Array.from({ length: 4 }, (_, i) => (
              <Skeleton key={i} className="h-4 w-2/3" />
            ))}
          </div>
        ) : error ? (
          <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
        ) : !user ? (
          <ErrorState title={t.states.notFoundTitle} />
        ) : (
          <div className="max-w-xl rounded-lg border border-border-default bg-surface-2 p-4">
            <Field label={t.col.phone}>
              {user.phone ? <PIIField value={user.phone} level="L2" /> : '—'}
            </Field>
            <Field label={t.users.email}>{user.email || '—'}</Field>
            <Field label={t.col.username}>{user.username ? `@${user.username}` : '—'}</Field>
            <Field label={t.col.country}>{user.country_code || '—'}</Field>
          </div>
        )}
      </div>
    </>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return <InfoRow label={label}>{children}</InfoRow>
}
